import { memo, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import InsightsWeekColumn from "./InsightsWeekColumn";
import { buildProgramInsightsPanelWeeks } from "../utils/insightsWeekSeries";
import { INSIGHTS_DATA_SOURCE } from "../utils/insightsSetRowMath";
import { resolveAllProgramWeeksExercises } from "../utils/resolveProgramWeekExercises";
import { useProgramInsightsCrossColumnRowSync } from "../hooks/useProgramInsightsCrossColumnRowSync";
import { computeMuscleBarGlobalMax } from "../utils/insightsMuscleBarLayout";

/**
 * Expanded panel body: horizontal row of week columns, synced in width with the
 * program builder weeks row. Vertical scroll is on `.ProgramInsights-panel` (not per week);
 * horizontal scroll + width sync lives in ProgramInsightsShell.
 * All metrics are computed from resolved program exercises.
 *
 * @param {(val: number, units?: string) => unknown} [props.fromMetric]
 * @param {"metric"|"imperial"} [props.insightsDisplayUnits]
 * @param {"rpe"|"rir"} [props.insightsIntensityMeasure]
 * @param {string} [props.insightsDataSource]
 */
function ProgramInsightsPanelContent({
  lens,
  builderWeeks,
  programExercises,
  weekHeaderLabels,
  fromMetric,
  insightsDisplayUnits = "metric",
  insightsIntensityMeasure = "rpe",
  insightsDataSource,
}) {
  const { t } = useTranslation("athlete-portal");

  const programInsightsResolution = useMemo(
    () => resolveAllProgramWeeksExercises(builderWeeks, programExercises),
    [builderWeeks, programExercises]
  );

  const unitsKey =
    insightsDisplayUnits === "imperial" ? "imperial" : "metric";
  const insightsVolumeSuffix = unitsKey === "imperial" ? " lb" : " kg";
/** Function to build program insights panel weeks.
 * @returns {object[]}
 */
  const panelWeeks = useMemo(
    () =>
      buildProgramInsightsPanelWeeks(programInsightsResolution, {
        fromMetric,
        unitsKey,
        intensityMeasure: insightsIntensityMeasure,
        insightsVolumeSuffix,
        dataSource: insightsDataSource ?? INSIGHTS_DATA_SOURCE.PRESCRIBED,
      }),
    [
      programInsightsResolution,
      fromMetric,
      unitsKey,
      insightsIntensityMeasure,
      insightsVolumeSuffix,
      insightsDataSource,
    ]
  );

  /** Function to compute the muscle bar global max.
   * @returns {number}
   */
  const muscleBarGlobalMax = useMemo(
    () => computeMuscleBarGlobalMax(panelWeeks),
    [panelWeeks]
  );

  const panelGridRef = useProgramInsightsCrossColumnRowSync(true);

  return (
    <section
      ref={panelGridRef}
      className="ProgramInsights-panelGrid"
      aria-label={t("programs.programInsights.panel.regionAria")}
    >
      {panelWeeks.map((week) => (
        <InsightsWeekColumn
          key={week.weekIndex}
          week={week}
          weekHeaderLabel={weekHeaderLabels?.[week.weekIndex]}
          lens={lens}
          insightsVolumeSuffix={insightsVolumeSuffix}
          insightsIntensityMeasure={insightsIntensityMeasure}
          muscleBarGlobalMax={muscleBarGlobalMax}
        />
      ))}
    </section>
  );
}

ProgramInsightsPanelContent.propTypes = {
  lens: PropTypes.string.isRequired,
  builderWeeks: PropTypes.object,
  programExercises: PropTypes.array,
  weekHeaderLabels: PropTypes.object,
  fromMetric: PropTypes.func,
  insightsDisplayUnits: PropTypes.oneOf(["metric", "imperial"]),
  insightsIntensityMeasure: PropTypes.oneOf(["rpe", "rir"]),
  insightsDataSource: PropTypes.oneOf([
    INSIGHTS_DATA_SOURCE.PRESCRIBED,
    INSIGHTS_DATA_SOURCE.OUTCOMES,
  ]),
};

export default memo(ProgramInsightsPanelContent);
